import {useEffect, useRef, useMemo, useCallback} from 'react'
import {Dialog, DialogTitle, DialogContent, Box, Typography, IconButton} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import {useTranslation} from 'react-i18next'
import {useAppTokens} from '../../theme'
import type {ThemeTokens} from '../../theme'
import {DREI_HDRI_CDN, DREI_PRESETS} from '../../../common/constants/envPresets'

// ── Thumbnail cache — persists across modal opens ───────────────

const thumbnailCache = new Map<string, string>()

// ── Single preset thumbnail — loads HDR + tone-maps to canvas ───

const THUMB_WIDTH = 160
const THUMB_ASPECT = 2

const EnvThumbnail = ({
  presetName,
  url,
  selected,
  onSelect,
}: {
  presetName: string
  url: string
  selected: boolean
  onSelect: (name: string) => void
}) => {
  const tokens = useAppTokens()
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    // Use cached version if available
    const cached = thumbnailCache.get(presetName)
    if (cached) {
      const img = new Image()
      img.onload = () => {
        canvas.width = THUMB_WIDTH
        canvas.height = THUMB_WIDTH / THUMB_ASPECT
        const ctx = canvas.getContext('2d')!
        ctx.drawImage(img, 0, 0)
      }
      img.src = cached
      return
    }

    let disposed = false

    const loadAndRender = async () => {
      const THREE = await import('three')
      const {HDRLoader} = await import('three/addons/loaders/HDRLoader.js')

      const loader = new HDRLoader()
      loader.setDataType(THREE.FloatType)

      loader.load(url, (texture) => {
        if (disposed) {
          texture.dispose()
          return
        }
        if (!canvas) {
          texture.dispose()
          return
        }

        const img = texture.image
        const aspect = img.width / img.height
        const cw = THUMB_WIDTH
        const ch = Math.round(cw / aspect)
        canvas.width = cw
        canvas.height = ch
        const ctx = canvas.getContext('2d')!

        const data = img.data as Float32Array
        const channels = data.length / (img.width * img.height)
        const imageData = ctx.createImageData(cw, ch)
        const pixels = imageData.data

        for (let y = 0; y < ch; y++) {
          for (let x = 0; x < cw; x++) {
            const sx = Math.floor((x / cw) * img.width)
            const sy = Math.floor((y / ch) * img.height)
            const srcIdx = (sy * img.width + sx) * channels

            let r = data[srcIdx] || 0
            let g = data[srcIdx + 1] || 0
            let b = data[srcIdx + 2] || 0

            // Reinhard tone map + gamma correction
            r = Math.pow(r / (1 + r), 1 / 2.2)
            g = Math.pow(g / (1 + g), 1 / 2.2)
            b = Math.pow(b / (1 + b), 1 / 2.2)

            const dstIdx = (y * cw + x) * 4
            pixels[dstIdx] = Math.min(255, r * 255)
            pixels[dstIdx + 1] = Math.min(255, g * 255)
            pixels[dstIdx + 2] = Math.min(255, b * 255)
            pixels[dstIdx + 3] = 255
          }
        }

        ctx.putImageData(imageData, 0, 0)
        texture.dispose()

        // Cache the rendered thumbnail
        thumbnailCache.set(presetName, canvas.toDataURL())
      })
    }

    loadAndRender()
    return () => {
      disposed = true
    }
  }, [presetName, url])

  const handleClick = useCallback(() => {
    onSelect(presetName)
  }, [presetName, onSelect])

  return (
    <Box
      onClick={handleClick}
      sx={{
        cursor: 'pointer',
        borderRadius: '6px',
        overflow: 'hidden',
        border: selected ? `2px solid ${tokens.text.primary}` : `2px solid transparent`,
        transition: 'border-color 0.15s',
        '&:hover': {
          borderColor: selected ? tokens.text.primary : tokens.text.muted,
        },
      }}
    >
      <canvas
        ref={canvasRef}
        style={{width: '100%', height: 'auto', display: 'block', backgroundColor: tokens.accordion.border}}
      />
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: selected ? 600 : 400,
          color: selected ? tokens.text.primary : tokens.text.secondary,
          textAlign: 'center',
          padding: '4px 0',
          textTransform: 'capitalize',
        }}
      >
        {presetName}
      </Typography>
    </Box>
  )
}

// ── EnvPresetModal ──────────────────────────────────────────────

const EnvPresetModal = ({
  open,
  onClose,
  onSelect,
  currentPreset,
}: {
  open: boolean
  onClose: () => void
  onSelect: (presetName: string) => void
  currentPreset: string
}) => {
  const {t} = useTranslation()
  const tokens = useAppTokens()
  const sx = useMemo(() => buildStyles(tokens), [tokens])

  const presetEntries = useMemo(
    () => Object.keys(DREI_PRESETS).map((name) => ({name, url: DREI_HDRI_CDN + DREI_PRESETS[name]})),
    [],
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth slotProps={{paper: {sx: sx.paper}}}>
      <DialogTitle sx={sx.title}>
        {t('scene.lighting.envMap')}
        <IconButton onClick={onClose} size="small" sx={sx.closeButton}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={sx.content}>
        <Box sx={sx.grid}>
          {presetEntries.map((entry) => (
            <EnvThumbnail
              key={entry.name}
              presetName={entry.name}
              url={entry.url}
              selected={entry.name === currentPreset}
              onSelect={onSelect}
            />
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  )
}

// ── Styles ───────────────────────────────────────────────────────

const buildStyles = (tokens: ThemeTokens) => ({
  paper: {
    backgroundColor: tokens.sidebar.background,
    backgroundImage: 'none',
  },
  title: {
    fontSize: 14,
    fontWeight: 600,
    color: tokens.text.primary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
  },
  closeButton: {
    color: tokens.text.muted,
  },
  content: {
    padding: '0 16px 16px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '8px',
  },
})

export default EnvPresetModal
